import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { addMinutes } from "date-fns";
import DateTimeField from "../../../Form/DateTimeField";
import TimezoneSelectField from "./TimezoneSelectField";
import LoadingDots from "./LoadingDots";
import {
  MODAL_MAIN_GENERATE_SOCIAL_MEDIA_CONTENT_SCHEDULE_BUTTON,
  BUTTON_PUBLISH_NOW,
  SCHEDULE_DATE_MIN_FUTURE_ERROR,
  SUGGESTED_PUBLISH_SCHEDULE_LOADING,
  TIMEZONE_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import {
  getTimezoneOptions,
  getZonedStartOfDay,
  resolveTimezone,
  toUtcTimestamp,
} from "../../../../utils/timezone";

const ScheduleDateSection = ({
  suggestedScheduleReason = "",
  isLoadingSuggestedSchedule = false,
}) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();

  const timezone = watch("timezone");
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const resolvedTimezone = resolveTimezone(timezone);
  const minDate = useMemo(
    () => getZonedStartOfDay({ timezone: resolvedTimezone }),
    [resolvedTimezone]
  );

  const validationRules = {
    validate: (value) => {
      if (!value) {
        return true;
      }

      const scheduledTimestamp = toUtcTimestamp({
        date: value,
        timezone: resolvedTimezone,
      });
      const minFutureTimestamp = addMinutes(new Date(), 5).getTime();

      if (
        scheduledTimestamp != null &&
        scheduledTimestamp < minFutureTimestamp
      ) {
        return t(SCHEDULE_DATE_MIN_FUTURE_ERROR);
      }

      return true;
    },
  };

  const onDateChange = (date) => {
    setValue("scheduledDate", date, { shouldValidate: true });
    trigger("scheduledDate");
  };

  const suggestionCaptionSx = {
    lineHeight: 1.35,
    display: "block",
    px: 1,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TimezoneSelectField
        control={control}
        errors={errors}
        name="timezone"
        label={t(TIMEZONE_PLACEHOLDER)}
        options={timezoneOptions}
      />

      <DateTimeField
        name="scheduledDate"
        control={control}
        label={t(MODAL_MAIN_GENERATE_SOCIAL_MEDIA_CONTENT_SCHEDULE_BUTTON)}
        minDate={minDate}
        fullWidth={true}
        variant="filled"
        clearable={true}
        errors={errors}
        rules={validationRules}
        actionsChange={onDateChange}
        showStartNowButton={true}
        startNowSetsNull={true}
        nowCustomPlaceholder={BUTTON_PUBLISH_NOW}
      />

      {isLoadingSuggestedSchedule ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={suggestionCaptionSx}
        >
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.25 }}
          >
            {t(SUGGESTED_PUBLISH_SCHEDULE_LOADING)}
            <LoadingDots />
          </Box>
        </Typography>
      ) : suggestedScheduleReason ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={suggestionCaptionSx}
        >
          {suggestedScheduleReason}
        </Typography>
      ) : null}
    </Box>
  );
};

export default ScheduleDateSection;
